import { RoleType } from '@/graphql/generated/types';
import { useRoleGuard } from '@/auth/role/role.hooks';
import { CopyId } from '@/ui/copy-id/copy-id.component';
import { Form } from '@/ui/form/form.component';
import { Grid } from '@/ui/grid/grid.component';
import { useConfirm } from '@/ui/modal/modal.hooks';
import { Section } from '@/ui/section/section.component';
import { error, success } from '@/ui/toaster/toast';
import { yupResolver } from '@hookform/resolvers/yup';
import { RouteComponentProps, navigate } from '@reach/router';
import React, { ComponentType, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useResources } from 'shared/resources/resources.provider';
import { object, string } from 'yup';
import { Frame } from '../../../frame';
import { Button } from '../../../ui/button/button.component';
import {
  useCreateReportMutation,
  useDeleteReportMutation,
  useEditReportMutation,
  useReportQuery,
} from './editor.generated';

type Values = {
  title: string;
  content?: string | null;
};

const schema = object({
  title: string().required(),
  content: string().nullable(),
});

const updateSchema = object({
  title: string().required(),
  content: string().nullable(),
});

type ReportEditorParams = {
  id?: string;
};

export const Editor: ComponentType<RouteComponentProps<ReportEditorParams>> = (
  props,
) => {
  const { hasRole, isSuper } = useRoleGuard();
  const isAdmin = isSuper || !!hasRole(RoleType.Admin);

  const reportQuery = useReportQuery({
    variables: { id: props.id },
    skip: props.id === undefined,
  });

  const [save] = useEditReportMutation();
  const [create] = useCreateReportMutation();
  const [deleteReport] = useDeleteReportMutation();

  const form = useForm<Values>({
    shouldUnregister: true,
    resolver: yupResolver(props.id ? updateSchema : schema),
  });
  const { handleSubmit } = form;

  const onSubmit = useMemo(() => {
    return handleSubmit(
      async (input) => {
        try {
          if (props.id) {
            await save({
              variables: {
                id: props.id,
                input: { title: input.title, content: input.content },
              },
            });
            success('Der Bericht wurde gespeichert.');
          } else {
            const report = await create({
              variables: {
                input: {
                  title: input.title,
                  content: input.content,
                },
              },
            });
            navigate(`./${report.data?.adminCreateReport.id}/edit`);
            success('Der Bericht wurde erstellt.');
          }
        } catch (e) {
          error('Beim Speichern des Berichts ist ein Fehler aufgetreten.');
        }
      },
      (e) => {
        error('Ein Fehler ist aufgetreten.');
        console.error(e);
      },
    );
  }, [props.id, handleSubmit]);

  const [modal, onAskDelete] = useConfirm(
    async () => {
      try {
        if (reportQuery.data?.adminReport.id) {
          await deleteReport({
            variables: { id: reportQuery.data.adminReport.id },
          });
          navigate('/reports');
          success('Der Bericht wurde gelöscht.');
        }
      } catch (e) {
        error('Beim Löschen des Berichts ist ein Fehler aufgetreten.');
      }
    },
    {
      title: `${reportQuery.data?.adminReport.title} löschen`,
      meaning: 'warning',
    },
  );

  const persisted = props.id !== undefined || reportQuery.data !== undefined;

  return (
    <>
      {modal}
      <Frame.Title>Berichte</Frame.Title>
      <Frame.SubTitle>
        {persisted ? reportQuery.data?.adminReport.title : 'Neuer Bericht'}
      </Frame.SubTitle>
      <Frame.ActionBar
        left={
          <CopyId
            label="ID"
            id={reportQuery.data?.adminReport.id}
            copyLabel={reportQuery.data?.adminReport.id}
          />
        }
      >
        <Button label="Zurück" linkTo="/reports" />
        {isAdmin && persisted && (
          <Button
            error
            label="Löschen"
            onClick={() =>
              onAskDelete('Soll dieser Bericht wirklich gelöscht werden?')
            }
          />
        )}
        {isAdmin && <Button primary label="Speichern" onClick={onSubmit} />}
      </Frame.ActionBar>
      <Frame.Content>
        {isAdmin ? (
          <Grid.Row>
            <Grid.Column>
              <Form form={form} values={reportQuery.data?.adminReport as any}>
                <Grid.Row>
                  <Grid.Column>
                    <Form.Input name="title" label="Titel" />
                    <Form.Textarea name="content" label="Inhalt" />
                  </Grid.Column>
                </Grid.Row>
              </Form>
            </Grid.Column>
            <Grid.Column>
              <div
                dangerouslySetInnerHTML={{
                  __html: reportQuery.data?.adminReport.content || '',
                }}
              />
            </Grid.Column>
          </Grid.Row>
        ) : (
          <Grid.Row>
            <Grid.Column>
              <Section title={reportQuery.data?.adminReport.title}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: reportQuery.data?.adminReport.content || '',
                  }}
                />
              </Section>
            </Grid.Column>
          </Grid.Row>
        )}
      </Frame.Content>
    </>
  );
};
